import { keyBy } from 'lodash-es';
import type { QueryKey, UseQueryOptions } from '@soundxyz/graphql-react-query/reactQuery';
import { useQuery } from '@soundxyz/graphql-react-query/reactQuery';
import { MINT_GAS_LIMIT_MULTIPLIER } from '@soundxyz/sdk/utils/constants';
import { scaleAmount } from '@soundxyz/sdk/utils/helpers';
import type { SuperMinterSchedule } from '@soundxyz/tiered-sdk-viem';
import type { GetEditionContractInfoReturnType } from '@soundxyz/tiered-sdk-viem/actions/public/getEditionContractInfo';
import type { Maybe } from '@soundxyz/utils';
import { getManyFromList } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import type { ContractType } from '@graphql/reactQuery';
import { GraphQLReactQuery } from '@graphql/reactQuery';
import { useTieredSdk } from '@hooks/useTieredSdk';
import { getMerkleProof } from '@utils/merkle';
import { expectNormalGas, SDK_KEY } from './helpers';

export type SuperMinterScheduleWithTier = SuperMinterSchedule & {
  tierInfo: NonNullable<GetEditionContractInfoReturnType['tierInfo'][number]>;
};

export function useTieredEditionMintSchedules({
  editionAddress,
  chainId,
  auctionContractType,
}: {
  editionAddress: string | undefined;
  chainId: number | undefined;
  auctionContractType: ContractType | null;
}) {
  const { tieredProvider } = useTieredSdk();

  const enabled =
    editionAddress != null && chainId != null && auctionContractType === 'TIERED_EDITION';

  return useQuery({
    queryKey: [SDK_KEY, 'tiered-schedules', editionAddress, chainId],
    async queryFn(): Promise<SuperMinterScheduleWithTier[]> {
      if (!enabled) return [];

      assertAddress(editionAddress);

      const provider = tieredProvider(chainId);

      const [tierData, schedules] = await Promise.all([
        GraphQLReactQuery.fetchQuery({
          queryKey: tieredEditionInfoQueryKey({ editionAddress, chainId }),
          queryFn() {
            return tieredEditionInfoQueryFn({
              editionAddress,
              chainId,
              tieredProvider,
            });
          },
          staleTime: 0,
        }).then(data => keyBy(data.tierInfo, v => v.tier)),
        provider
          .getMintingSchedules({
            editionAddress,
          })
          .then(data => data.schedules.map(v => v)),
      ]);

      return getManyFromList(
        schedules.map(v => v),
        schedule => {
          const tierInfo = tierData[schedule.tier];

          if (!tierInfo) return null;

          return {
            ...schedule,
            tierInfo,
          };
        },
      );
    },
    enabled,
    staleTime: 0,
    select(tieredSchedules) {
      const now = Math.floor(Date.now() / 1000);

      return {
        tieredSchedules,
        activeTieredSchedules: tieredSchedules.filter(
          schedule => schedule.startTime <= now && schedule.endTime > now && !schedule.paused,
        ),
      };
    },
  });
}

export function useTieredEditionInfo({
  editionAddress,
  chainId,
  auctionContractType,
}: {
  editionAddress: string | undefined;
  chainId: number | undefined;
  auctionContractType: ContractType | null;
}) {
  const { tieredProvider } = useTieredSdk();

  const enabled =
    editionAddress != null && chainId != null && auctionContractType === 'TIERED_EDITION';

  return useQuery({
    queryKey: enabled ? tieredEditionInfoQueryKey({ editionAddress, chainId }) : undefined,
    queryFn() {
      if (!enabled) return;

      return tieredEditionInfoQueryFn({
        editionAddress,
        chainId,
        tieredProvider,
      });
    },
    enabled,
    staleTime: 0,
  });
}

export function useEstimateTieredMintGas({
  mintSchedule,
  chainId,
  quantity,
  editionAddress,
  totalValue,
}: {
  mintSchedule: SuperMinterScheduleWithTier;
  chainId: number;
  quantity: number;
  editionAddress: string;
  totalValue: Maybe<bigint>;
}) {
  const { authUserAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const result = useQuery({
    staleTime: Infinity,
    cacheTime: Infinity,
    queryKey: [
      SDK_KEY,
      'tiered-mint-estimate',
      editionAddress,
      quantity,
      authUserAddress,
      chainId,
      mintSchedule.tier,
      mintSchedule.scheduleNum,
    ],
    async queryFn() {
      if (!authUserAddress || !totalValue) return null;

      assertAddress(editionAddress);

      const allowlistProof =
        mintSchedule.mode === 'VERIFY_MERKLE'
          ? await getMerkleProof({
              merkleRoot: mintSchedule.merkleRoot,
              userAddress: authUserAddress,
            })
          : null;

      if (mintSchedule.mode === 'VERIFY_MERKLE') {
        if (!allowlistProof) throw Error('Not eligible!');
      }

      const gasEstimate = await tieredProvider(chainId).estimateGasMintTieredEdition({
        account: authUserAddress,
        edition: editionAddress,
        mintParams:
          mintSchedule.mode === 'VERIFY_MERKLE' && allowlistProof
            ? {
                mode: 'VERIFY_MERKLE',
                allowlisted: authUserAddress,
                allowlistProof,
              }
            : {
                mode: 'DEFAULT',
              },
        quantity,
        scheduleNum: mintSchedule.scheduleNum,
        tier: mintSchedule.tier,
        to: authUserAddress,
        value: totalValue,
      });

      const gasLimit = expectNormalGas({
        estimation: scaleAmount({
          amount: gasEstimate,
          multiplier: MINT_GAS_LIMIT_MULTIPLIER,
        }),
        editionAddress,
        chainId,
        quantity,
        loggingExtra: {
          mintSchedule,
        },
      });

      return {
        gasLimit,
      };
    },
    enabled: !!authUserAddress && !!quantity && !!totalValue,
  });

  return {
    ...result,
    isMintEstimationLoadingWithoutErrors: result.isInitialLoading && result.failureCount === 0,
  };
}

export function useTieredEditionEligibleQuantity<QueryData = bigint | null>(
  {
    mintSchedule,
    chainId,
    editionAddress,
  }: {
    mintSchedule: Maybe<SuperMinterScheduleWithTier>;
    chainId: number;
    editionAddress: string;
  },
  options?: Omit<
    UseQueryOptions<
      bigint | null,
      Error,
      QueryData,
      ReturnType<typeof tieredEligibleQuantityQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  const { authUserAddress: userAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const getTieredEligibleQuantity = useStableCallback(
    async ({
      mintSchedule,
      userAddress,
      editionAddress,
    }: {
      mintSchedule: Maybe<SuperMinterScheduleWithTier>;
      userAddress: Maybe<string>;
      editionAddress: Maybe<string>;
    }) => {
      if (!mintSchedule || !userAddress || !editionAddress) return 0n;

      assertAddress(userAddress);
      assertAddress(editionAddress);

      const eligibleQuantity = await tieredProvider(chainId)
        .getMintEligibility({
          collectorAddress: userAddress,
          editionAddress,
          scheduleNum: mintSchedule.scheduleNum,
          tier: mintSchedule.tier,
          merkleProvider: {
            merkleProof: ({ merkleRoot, userAddress }) =>
              getMerkleProof({
                merkleRoot,
                userAddress,
              }),
          },
        })
        .then(v => BigInt(v.remainingEligibility));

      GraphQLReactQuery.setQueryData(
        tieredEligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
        eligibleQuantity,
      );

      return eligibleQuantity;
    },
  );

  const result = useQuery({
    queryKey: tieredEligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
    queryFn() {
      return getTieredEligibleQuantity({
        mintSchedule,
        userAddress,
        editionAddress,
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && !!mintSchedule && !!userAddress,
  });

  return {
    ...result,
    getTieredEligibleQuantity,
  };
}

export function useTieredMintPrice({
  editionAddress,
  mintSchedule,
  chainId,
  quantity,
}: {
  editionAddress: string;
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  chainId: number;
  quantity: number;
}) {
  const { tieredProvider } = useTieredSdk();

  const { data: totalMintPriceWei } = useQuery({
    queryKey: [
      SDK_KEY,
      'tiered-mint-price',
      editionAddress,
      chainId,
      mintSchedule?.tier,
      mintSchedule?.scheduleNum,
      quantity,
    ],
    async queryFn() {
      if (!mintSchedule) return null;

      assertAddress(editionAddress);

      const { total, subTotal } = await tieredProvider(chainId).getTotalMintPriceAndFees({
        quantity,
        scheduleNum: mintSchedule.scheduleNum,
        tier: mintSchedule.tier,
        edition: editionAddress,
      });

      return { total, subTotal, fees: total - subTotal };
    },
    enabled: !!mintSchedule,
  });

  return {
    totalMintPriceWei,
  };
}

function tieredEligibleQuantityQueryKey({
  mintSchedule,
  userAddress,
  editionAddress,
}: {
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  userAddress: Maybe<string>;
  editionAddress: Maybe<string>;
}): QueryKey {
  return [
    SDK_KEY,
    'tiered-edition-eligible-quantity',
    editionAddress,
    mintSchedule?.tier,
    mintSchedule?.scheduleNum,
    userAddress,
  ] as const;
}

function tieredEditionInfoQueryKey({
  editionAddress,
  chainId,
}: {
  editionAddress: string;
  chainId: number;
}) {
  return [SDK_KEY, 'tiered-info', editionAddress, chainId] as const;
}

function tieredEditionInfoQueryFn({
  editionAddress,
  chainId,
  tieredProvider,
}: {
  editionAddress: string;
  chainId: number;
  tieredProvider: ReturnType<typeof useTieredSdk>['tieredProvider'];
}) {
  assertAddress(editionAddress);
  return tieredProvider(chainId).getEditionContractInfo({
    edition: editionAddress,
  });
}
